import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Icon,
  Paper,
  Popover,
  Slider,
  Snackbar,
  Typography,
} from "@mui/material";
import type * as mapboxgl from "mapbox-gl";
import type { FeatureCollection, Geometry, MultiPolygon, Polygon } from "geojson";

import ToolbarButton from "../toolbar/ToolbarButton";
import { useMap } from "../../contexts/MapContext";
import { useTimezone } from "../../contexts/TimezoneContext";
import { formatInTimezone } from "../../utils/datetime";
import { Filter } from "../layerControl/FilterModal";
import { applyFilters } from "../../utils/layerFilters";
import {
  useCreateIncidentFromDetection,
  useProjectIncidents,
} from "../../api/incidentLog";
import {
  ALL_LEGEND_SLOTS,
  LEGEND_PRIORITY,
  LEGEND_SLOT_STYLES,
  LEGEND_WIDTH,
  stackedLegendStyle,
  useLegendLayout,
  useLegendSlot,
} from "./legendSlots";
import {
  useRealtimeObservations,
  useDeleteSimulatedDetection,
  roundBboxOutward,
  RealtimeBbox,
  RealtimeKind,
  RealtimeWindow,
} from "../../api/realtimeObservations";
import CreateIgnitionFromDetectionsDialog, {
  PendingIgnition,
} from "../CreateIgnitionFromDetectionsDialog";
import {
  clusterDetections,
  clustersToFeatureCollection,
  dedupeDetections,
  pointInGeometry,
  DEFAULT_DELTA_M,
  MAX_DELTA_M,
  MIN_DELTA_M,
} from "../../utils/detectionClusters";
import { matchClustersToIncidents } from "../../utils/clusterIncidents";
import { mapEventScreenPoint } from "../../utils/uiZoom";
import {
  AgeColorStop,
  BURNT_AREA_AGE_COLOR_STOPS,
  HAZARD_LEVEL_STYLES,
  HOTSPOT_AGE_COLOR_STOPS,
  IGNITION_AGE_COLOR_STOPS,
  LIGHTNING_NEGATIVE_AGE_STOPS,
  LIGHTNING_POLARITY_RAMPS,
  HOTSPOT_FRP_SIZE_STOPS,
  burntAreaFillStyle,
  burntAreaLayerStyle,
  burntAreaLineStyle,
  hazardAreaFillStyle,
  hazardAreaLineStyle,
  hazardLayerStyle,
  hotspotLayerStyle,
  hotspotClusterFillStyle,
  hotspotClusterLineStyle,
  ignitionLayerStyle,
  lightningLayerStyle,
} from "./realtimeStyles";

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

const WINDOW_OPTIONS: RealtimeWindow[] = ["1h", "6h", "24h", "72h"];

const KIND_META: Record<
  RealtimeKind,
  { title: string; icon: string; ageStops: AgeColorStop[] }
> = {
  hotspot: {
    title: "Satellite Hotspots",
    icon: "local_fire_department",
    ageStops: HOTSPOT_AGE_COLOR_STOPS,
  },
  lightning: {
    title: "Lightning Strikes",
    icon: "bolt",
    ageStops: LIGHTNING_NEGATIVE_AGE_STOPS,
  },
  hazard: {
    title: "Hazards Incidents",
    icon: "warning",
    // Unused: hazards are coloured by alert level, not age (see the legend).
    ageStops: HOTSPOT_AGE_COLOR_STOPS,
  },
  ignition: {
    title: "Ignition Points",
    icon: "whatshot",
    ageStops: IGNITION_AGE_COLOR_STOPS,
  },
  burnt_area: {
    title: "Burnt Areas",
    icon: "landscape",
    ageStops: BURNT_AREA_AGE_COLOR_STOPS,
  },
};

// Kinds whose features are NOT re-scanned pixels: deduping by location would
// merge distinct reports, and for the polygon kinds it would drop every area
// feature (dedupeDetections keeps Points only).
const SKIP_DEDUPE_KINDS = new Set<RealtimeKind>(["hazard", "ignition", "burnt_area"]);

// Served-feature keys that are plumbing rather than something the reporter
// typed into their ArcGIS layer; everything else on a customer-fed feature
// is shown in the popover as-is.
const NON_ATTRIBUTE_KEYS = new Set([
  "source_id",
  "source_name",
  "observed_at",
  "observed_at_epoch",
  "age_minutes",
  "hoverLabel",
  "is_area",
  "incident_label",
  "incident_id",
  "simulated",
  "expires_at",
  "expires_in_minutes",
  "observation_count",
  "position_uncertainty_m",
  "sensor_family",
  "pixel_m",
  "orbit",
  "resolution_known",
  "title",
  "layer_name",
  "object_id",
  "global_id",
  "feature_url",
  "area_ha",
  "geometry_type",
  "level",
]);
const MAX_ATTRIBUTE_ROWS = 10;

const customAttributeRows = (
  properties: Record<string, any>
): { label: string; value: string }[] => {
  const rows: { label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(properties)) {
    if (rows.length >= MAX_ATTRIBUTE_ROWS) break;
    if (key.startsWith("__") || NON_ATTRIBUTE_KEYS.has(key)) continue;
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "object") continue;
    rows.push({ label: key.replace(/_/g, " "), value: String(value) });
  }
  return rows;
};

// The part of a (Multi)Polygon worth igniting from: a MultiPolygon becomes
// its largest part, since an ignition is one polygon.
const largestPolygon = (geometry: Geometry | undefined | null): Polygon | null => {
  if (!geometry) return null;
  if (geometry.type === "Polygon") return geometry;
  if (geometry.type !== "MultiPolygon") return null;
  const ringArea = (ring: number[][]) => {
    let area = 0;
    for (let i = 0; i < ring.length - 1; i += 1) {
      area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
    }
    return Math.abs(area) / 2;
  };
  let best: Polygon | null = null;
  let bestArea = -1;
  for (const rings of (geometry as MultiPolygon).coordinates) {
    if (!rings.length) continue;
    const area = ringArea(rings[0]);
    if (area > bestArea) {
      bestArea = area;
      best = { type: "Polygon", coordinates: rings };
    }
  }
  return best;
};

// Current-state kinds are not a time series — the backend serves every
// active item regardless of age and drops it when the source does — so the
// time-window selector is meaningless for them. (Burnt areas are polled as a
// snapshot by default: a perimeter mapped a week ago is still the current
// burnt area.)
const CURRENT_STATE_KINDS = new Set<RealtimeKind>(["hazard", "burnt_area"]);
const KIND_HAS_WINDOW = (kind: RealtimeKind) => !CURRENT_STATE_KINDS.has(kind);

const sameRoundedBbox = (a: RealtimeBbox | null, b: RealtimeBbox | null) => {
  if (!a || !b) return a === b;
  const ra = roundBboxOutward(a);
  const rb = roundBboxOutward(b);
  return (
    ra.top === rb.top &&
    ra.left === rb.left &&
    ra.bottom === rb.bottom &&
    ra.right === rb.right
  );
};

const formatAge = (ageMinutes: number | null | undefined): string => {
  if (ageMinutes === null || ageMinutes === undefined || isNaN(Number(ageMinutes))) {
    return "";
  }
  const mins = Math.max(0, Math.round(Number(ageMinutes)));
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hours} h ${rem} min ago` : `${hours} h ago`;
};

interface RealtimeLegendProps {
  kind: RealtimeKind;
  windowHours: RealtimeWindow;
  onWindowChange: (w: RealtimeWindow) => void;
  /** Feature count currently on the map — an empty area and a broken layer
   *  look identical without this. */
  count: number;
  isLoading: boolean;
  scopeNote?: string;
  /** Cluster controls: hotspots on the project map only. */
  clusterDelta?: number;
  onClusterDeltaChange?: (delta: number) => void;
  clusterCount?: number;
}

// Legend card. Lightning takes the far-left corner; if another legend is
// already there — the UrbanLegend, which is fixed to that corner and cannot
// move — it falls back to the next slot along. Hotspots take whatever is left,
// so with everything open the order is urban, lightning, hotspots.
const RealtimeLegend: React.FC<RealtimeLegendProps> = ({
  kind,
  windowHours,
  onWindowChange,
  count,
  isLoading,
  scopeNote,
  clusterDelta,
  onClusterDeltaChange,
  clusterCount = 0,
}) => {
  const meta = KIND_META[kind];
  const slot = useLegendSlot(
    `realtime-${kind}`,
    ALL_LEGEND_SLOTS,
    LEGEND_PRIORITY[kind] ?? LEGEND_PRIORITY.hotspot
  );
  // Stacked layout (the listing's half-width pane): a one-line chip until
  // the pointer rests on it, then the full card grows upward from the chip.
  const layout = useLegendLayout();
  const [open, setOpen] = useState(false);
  const stacked = layout === "stacked";
  const compact = stacked && !open;
  const cardSx = {
    position: "absolute" as const,
    ...(stacked ? stackedLegendStyle(slot) : LEGEND_SLOT_STYLES[slot]),
    width: compact ? "auto" : LEGEND_WIDTH,
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    borderRadius: "10px",
    padding: compact ? "5px 10px" : "10px 12px",
    zIndex: stacked && open ? 1001 : 1000,
    color: "#f8fafc",
    fontSize: "12px",
  };

  return (
    <Box
      sx={cardSx}
      onMouseEnter={stacked ? () => setOpen(true) : undefined}
      onMouseLeave={stacked ? () => setOpen(false) : undefined}
    >
      {compact ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
          <Icon sx={{ fontSize: 16, color: "#cbd5e1" }}>{meta.icon}</Icon>
          <Typography sx={{ fontWeight: 600, fontSize: "0.72rem" }}>{meta.title}</Typography>
          <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>
            {isLoading ? "…" : count}
          </Typography>
        </Box>
      ) : (
      <>
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.75 }}>
        {meta.title}
      </Typography>

      {/* Age colour ramp. Lightning shows one ramp per polarity, because its
          fill encodes both (hue = polarity, fade = age). Hazards ignore age
          entirely and key on the issuing agency's alert level. */}
      <Typography
        sx={{
          color: "#94a3b8",
          fontWeight: 600,
          textTransform: "uppercase",
          fontSize: "0.6rem",
          mb: 0.5,
        }}
      >
        {kind === "lightning"
          ? "Age & polarity"
          : kind === "hazard"
          ? "Alert level"
          : "Age"}
      </Typography>

      {kind === "hazard" ? (
        <Box sx={{ mb: 1 }}>
          {HAZARD_LEVEL_STYLES.map((entry) => (
            <Box
              key={entry.level}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.35 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.62rem", color: "#cbd5e1" }}>
                {entry.label}
              </Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", mt: 0.5 }}>
            Shaded areas are the agency's warning areas.
          </Typography>
        </Box>
      ) : kind === "lightning" ? (
        <Box sx={{ mb: 1 }}>
          {LIGHTNING_POLARITY_RAMPS.map((ramp) => (
            <Box
              key={ramp.polarity}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.4 }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  color: "#cbd5e1",
                  width: 52,
                  flexShrink: 0,
                }}
              >
                {ramp.polarity} {ramp.label}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flex: 1 }}>
                {ramp.stops.map((stop) => (
                  <Box
                    key={stop.minutes}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: "2px",
                      backgroundColor: stop.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
          {/* Age ticks, aligned under the ramps (past the polarity labels) */}
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Box sx={{ width: 52, flexShrink: 0 }} />
            <Box sx={{ display: "flex", gap: 0.5, flex: 1 }}>
              {meta.ageStops.map((stop) => (
                <Typography
                  key={stop.minutes}
                  sx={{
                    flex: 1,
                    fontSize: "0.6rem",
                    color: "#cbd5e1",
                    textAlign: "center",
                  }}
                >
                  {stop.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
          {meta.ageStops.map((stop) => (
            <Box key={stop.minutes} sx={{ flex: 1, textAlign: "center" }}>
              <Box
                sx={{
                  height: 8,
                  borderRadius: "2px",
                  backgroundColor: stop.color,
                  mb: 0.25,
                }}
              />
              <Typography sx={{ fontSize: "0.6rem", color: "#cbd5e1" }}>
                {stop.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {kind === "burnt_area" && (
        <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", mb: 1 }}>
          Shaded areas are the mapped burnt footprints; the dot is each
          one's anchor point.
        </Typography>
      )}

      {kind === "hotspot" && (
        <>
          <Typography
            sx={{
              color: "#94a3b8",
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.6rem",
              mb: 0.5,
            }}
          >
            Fire power (FRP)
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            {HOTSPOT_FRP_SIZE_STOPS.map((stop) => (
              <Box
                key={stop.label}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    // Scaled down from the map radii so three swatches plus
                    // labels still fit the card width.
                    width: Math.round(stop.radius * 1.4),
                    height: Math.round(stop.radius * 1.4),
                    borderRadius: "50%",
                    backgroundColor: "#ff7a00",
                  }}
                />
                <Typography sx={{ fontSize: "0.6rem", color: "#cbd5e1" }}>
                  {stop.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Time window selector — hidden for current-state kinds. */}
      {KIND_HAS_WINDOW(kind) && (
      <Box sx={{ display: "flex", gap: 0.5, mb: 0.75 }}>
        {WINDOW_OPTIONS.map((w) => (
          <Box
            key={w}
            onClick={() => onWindowChange(w)}
            sx={{
              flex: 1,
              textAlign: "center",
              paddingY: "3px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.68rem",
              fontWeight: 600,
              color: windowHours === w ? "#0f172a" : "#cbd5e1",
              backgroundColor:
                windowHours === w ? "#00d4ad" : "rgba(148, 163, 184, 0.15)",
              "&:hover": {
                backgroundColor:
                  windowHours === w ? "#00d4ad" : "rgba(148, 163, 184, 0.3)",
              },
            }}
          >
            {w}
          </Box>
        ))}
      </Box>
      )}

      {onClusterDeltaChange && clusterDelta !== undefined && (
        <Box sx={{ mb: 0.75 }}>
          <Typography
            sx={{
              color: "#94a3b8",
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.6rem",
            }}
          >
            Fire grouping · {clusterCount} {clusterCount === 1 ? "fire" : "fires"}
          </Typography>
          <Slider
            size="small"
            value={clusterDelta}
            min={MIN_DELTA_M}
            max={MAX_DELTA_M}
            step={50}
            onChange={(_, value) => onClusterDeltaChange(value as number)}
            sx={{
              color: "#00d4ad",
              mt: 0.5,
              mb: 0,
              "& .MuiSlider-thumb": { width: 12, height: 12 },
            }}
          />
          <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
            {clusterDelta} m radius — detections merge within{" "}
            {(clusterDelta * 2) / 1000} km
          </Typography>
        </Box>
      )}

      {/* Count first: a blank map is otherwise indistinguishable from a
          broken layer. */}
      {isLoading ? (
        <Typography sx={{ fontSize: "0.68rem", color: "#cbd5e1" }}>
          Loading…
        </Typography>
      ) : count > 0 ? (
        <Typography sx={{ fontSize: "0.68rem", color: "#f8fafc" }}>
          <strong>{count.toLocaleString()}</strong>{" "}
          {kind === "hotspot"
            ? `hotspots in the last ${windowHours}`
            : kind === "lightning"
            ? `strikes in the last ${windowHours}`
            : kind === "ignition"
            ? `ignition points in the last ${windowHours}`
            : kind === "burnt_area"
            ? count === 1
              ? "mapped burnt area"
              : "mapped burnt areas"
            : count === 1
            ? "active incident"
            : "active incidents"}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: "0.68rem", color: "#fbbf24" }}>
          {kind === "hazard"
            ? "No active incidents"
            : kind === "burnt_area"
            ? "No mapped burnt areas"
            : `None in the last ${windowHours}`}
          {scopeNote ? ` ${scopeNote}` : ""}
        </Typography>
      )}

      <Typography sx={{ fontSize: "0.62rem", color: "#64748b" }}>
        live · updates every 60s
      </Typography>
      </>
      )}
    </Box>
  );
};

interface RealtimeObservationPopoverProps {
  kind: RealtimeKind;
  properties: Record<string, any> | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  /** Present only where an ignition can be created (project map, hotspots). */
  onCreateIgnition?: () => void;
  /** Shown in place of the button when creation is not allowed here. */
  blockedReason?: string;
  /** Present only for simulated detections: removes it ahead of its expiry. */
  onDeleteSimulated?: () => void;
  deleteInProgress?: boolean;
  /** Project map only: track this detection (and its recent neighbours) as
   *  an incident — the manual path for fires that predate the project or
   *  predate detections being switched on. */
  onCreateIncident?: () => void;
  createIncidentInProgress?: boolean;
  /** Burnt-area polygons on the project map: ignite from the mapped
   *  footprint itself. */
  onCreateAreaIgnition?: () => void;
}

const RealtimeObservationPopover: React.FC<RealtimeObservationPopoverProps> = ({
  kind,
  properties,
  anchorPosition,
  onClose,
  onCreateIgnition,
  blockedReason,
  onDeleteSimulated,
  deleteInProgress,
  onCreateIncident,
  createIncidentInProgress,
  onCreateAreaIgnition,
}) => {
  const { selectedTimezone } = useTimezone();
  const open = Boolean(properties && anchorPosition);

  if (!properties) return null;

  const observedAt = formatInTimezone(
    properties.observed_at,
    selectedTimezone,
    "D MMM YYYY, h:mm A"
  );
  const age = formatAge(properties.age_minutes);

  const rows: { label: string; value: string }[] = [];
  if (kind === "hotspot") {
    if (properties.satellite) rows.push({ label: "Satellite", value: String(properties.satellite) });
    if (properties.sensor) rows.push({ label: "Sensor", value: String(properties.sensor) });
    if (properties.confidence !== null && properties.confidence !== undefined) {
      rows.push({ label: "Confidence", value: `${properties.confidence}%` });
    }
    if (properties.frp !== null && properties.frp !== undefined) {
      rows.push({ label: "FRP", value: `${properties.frp} MW` });
    }
    if (properties.brightness !== null && properties.brightness !== undefined) {
      rows.push({ label: "Brightness", value: `${properties.brightness} K` });
    }
    if (Number(properties.observation_count) > 1) {
      rows.push({
        label: "Observations",
        value: `${properties.observation_count} (same pixel re-scanned)`,
      });
    }
  } else if (kind === "hazard") {
    if (properties.category) rows.push({ label: "Alert level", value: String(properties.category) });
    if (properties.event_type) rows.push({ label: "Type", value: String(properties.event_type) });
    if (properties.status) rows.push({ label: "Status", value: String(properties.status) });
    if (properties.location) rows.push({ label: "Location", value: String(properties.location) });
    if (properties.size_ha !== null && properties.size_ha !== undefined) {
      rows.push({ label: "Size", value: `${Number(properties.size_ha).toLocaleString()} ha` });
    }
    if (properties.agency) rows.push({ label: "Agency", value: String(properties.agency) });
  } else if (kind === "ignition" || kind === "burnt_area") {
    // Customer-fed features: the layer's own attributes are the content.
    if (properties.area_ha !== null && properties.area_ha !== undefined) {
      rows.push({
        label: "Area",
        value: `${Number(properties.area_ha).toLocaleString()} ha`,
      });
    }
    if (
      kind === "ignition" &&
      properties.position_uncertainty_m !== null &&
      properties.position_uncertainty_m !== undefined
    ) {
      rows.push({
        label: "Position",
        value: `±${Number(properties.position_uncertainty_m).toLocaleString()} m (as reported)`,
      });
    }
    if (properties.layer_name) {
      rows.push({ label: "Layer", value: String(properties.layer_name) });
    }
    rows.push(...customAttributeRows(properties));
  } else {
    if (properties.polarity) {
      rows.push({
        label: "Polarity",
        value: properties.polarity === "+" ? "Positive (+)" : "Negative (−)",
      });
    }
    if (properties.amplitude_ka !== null && properties.amplitude_ka !== undefined) {
      rows.push({ label: "Amplitude", value: `${properties.amplitude_ka} kA` });
    }
    if (properties.strike_type) {
      rows.push({
        label: "Strike type",
        value: properties.strike_type === "ground" ? "Cloud-to-ground" : "Cloud",
      });
    }
  }

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Paper sx={{ p: 2, maxWidth: 300, bgcolor: "#212b37", color: "white" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {kind === "hotspot"
            ? "Satellite Hotspot"
            : kind === "lightning"
            ? "Lightning Strike"
            : kind === "ignition"
            ? properties.title || "Ignition Point"
            : kind === "burnt_area"
            ? properties.title || "Burnt Area"
            : /* The incident's own title carries far more than a generic
                 heading would; the alert level appears in the rows below. */
              properties.title || "Hazards Incident"}
          {properties.simulated && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 0.75,
                py: 0.25,
                borderRadius: "6px",
                fontSize: "0.65rem",
                fontWeight: 700,
                verticalAlign: "middle",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: "#0f172a",
                backgroundColor: "#fbbf24",
              }}
            >
              Simulated
            </Box>
          )}
        </Typography>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {observedAt}
          {age && (
            <span style={{ color: "#94a3b8", marginLeft: 6 }}>({age})</span>
          )}
        </Typography>

        {properties.source_name && (
          <Typography variant="body2" sx={{ mb: 1, color: "#94a3b8" }}>
            {properties.source_name}
          </Typography>
        )}

        {rows.map((row) => (
          <Typography key={row.label} variant="body2" sx={{ mb: 0.25 }}>
            <span style={{ color: "#94a3b8" }}>{row.label}: </span>
            {row.value}
          </Typography>
        ))}

        {/* The agency's own advice text — the part a responder actually acts
            on. Scrolls rather than stretching the popover down the map. */}
        {kind === "hazard" && properties.description && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "#cbd5e1",
              fontSize: "0.75rem",
              maxHeight: 160,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {String(properties.description)}
          </Typography>
        )}

        {kind === "hazard" && properties.link && (
          <Button
            size="small"
            variant="outlined"
            fullWidth
            href={String(properties.link)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 1.5,
              color: "#cbd5e1",
              borderColor: "rgba(203, 213, 225, 0.5)",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Open the agency's page
          </Button>
        )}

        {(kind === "ignition" || kind === "burnt_area") && properties.feature_url && (
          <Button
            size="small"
            variant="outlined"
            fullWidth
            href={String(properties.feature_url)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 1.5,
              color: "#cbd5e1",
              borderColor: "rgba(203, 213, 225, 0.5)",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Open the feature in ArcGIS
          </Button>
        )}

        {onCreateAreaIgnition && (
          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={onCreateAreaIgnition}
            sx={{
              mt: 1.5,
              bgcolor: "#00d4ad",
              color: "#0f172a",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#00b89a" },
            }}
          >
            Create ignition from this area
          </Button>
        )}

        {/* Only simulated detections carry an expiry; server-computed each
            60s poll, so it counts down as the layer refreshes. */}
        {properties.expires_in_minutes !== null &&
          properties.expires_in_minutes !== undefined && (
            <Typography variant="body2" sx={{ mb: 0.25 }}>
              <span style={{ color: "#94a3b8" }}>Expires: </span>
              {Number(properties.expires_in_minutes) < 1
                ? "under a minute"
                : `in ${formatAge(properties.expires_in_minutes).replace(
                    " ago",
                    ""
                  )}`}
            </Typography>
          )}

        {blockedReason && (
          <Typography
            variant="body2"
            sx={{ mt: 1.5, color: "#fbbf24", fontSize: "0.75rem" }}
          >
            {blockedReason}
          </Typography>
        )}

        {onCreateIgnition && (
          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={onCreateIgnition}
            sx={{
              mt: 1.5,
              bgcolor: "#00d4ad",
              color: "#0f172a",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#00b89a" },
            }}
          >
            Create ignition here
          </Button>
        )}

        {onCreateIncident && (
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={onCreateIncident}
            disabled={createIncidentInProgress}
            sx={{
              mt: 1.5,
              color: "#fbbf24",
              borderColor: "#fbbf24",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#f59e0b",
                backgroundColor: "rgba(251, 191, 36, 0.08)",
              },
            }}
          >
            {createIncidentInProgress ? "Creating…" : "Create incident"}
          </Button>
        )}

        {onDeleteSimulated && (
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={onDeleteSimulated}
            disabled={deleteInProgress}
            sx={{
              mt: 1.5,
              color: "#f87171",
              borderColor: "#f87171",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#ef4444",
                backgroundColor: "rgba(248, 113, 113, 0.08)",
              },
            }}
          >
            {deleteInProgress ? "Removing…" : "Remove simulated detection"}
          </Button>
        )}
      </Paper>
    </Popover>
  );
};

export interface RealtimeObservationsLayerProps {
  id: string;
  name: string;
  kind: RealtimeKind;
  /** Returns the bbox to fetch observations for. Called with the live map
   *  instance (or null before it exists) and re-evaluated on debounced
   *  moveend, so it can read either a fixed project bbox or the viewport. */
  getBbox: (map: mapboxgl.Map | null) => RealtimeBbox | null;
  /** Whether this kind is live for the viewer's organisation (a data source
   *  exists and the tenant is entitled to it). There is no per-project
   *  opt-in: an available kind is available on every project. */
  available: boolean;
  filters?: Filter[];
  /** Appended to the "None in the last 24h" message so the user knows which
   *  area was searched (the project map queries a fixed buffered project
   *  bbox — zooming out does not widen it). */
  scopeNote?: string;
  /** Project map only: cluster hulls become clickable and detections offer
   *  "create ignition". Off on the listing map, which has no project or
   *  ignition-regime context. */
  projectIdForIgnitions?: number;
  /** Ignitions must lie inside this; the backend enforces it too. */
  projectBoundary?: { type?: string; coordinates?: any } | null;
  /** Increment to force the layer visible — used after placing a simulated
   *  detection so it doesn't land on a hidden layer and look like nothing
   *  happened. Ignored while detections are disabled. */
  activateSignal?: number;
}

const RealtimeObservationsLayer = ({
  id,
  name,
  kind,
  getBbox,
  available,
  filters = [],
  scopeNote,
  projectIdForIgnitions,
  projectBoundary,
  activateSignal,
}: RealtimeObservationsLayerProps) => {
  const { map } = useMap();
  const { selectedTimezone } = useTimezone();
  const [active, setActive] = useState(false);
  const [windowHours, setWindowHours] = useState<RealtimeWindow>("24h");
  const [clusterDelta, setClusterDelta] = useState(DEFAULT_DELTA_M);
  const [bbox, setBbox] = useState<RealtimeBbox | null>(null);
  // `stale` drives only the initial source registration in Map; data refreshes
  // go through map.getSource(id).setData(...) (IgnitionsLayer precedent) so
  // the layer never flickers on the 60s poll.
  const [stale, setStale] = useState(true);
  const [geojson, setGeojson] = useState<FeatureCollection>(EMPTY_FC);

  const [pendingIgnition, setPendingIgnition] = useState<PendingIgnition | null>(null);
  const [outsideNotice, setOutsideNotice] = useState<string | null>(null);
  const [selectedProps, setSelectedProps] = useState<Record<string, any> | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const getBboxRef = useRef(getBbox);
  getBboxRef.current = getBbox;

  // Track the bbox: project maps hand back a fixed (buffered) project bbox,
  // the listing map reads the live viewport. Two sync paths, both skipping the
  // state update when the rounded bbox is unchanged so tiny camera moves (or
  // re-renders) don't churn state or refetch:
  // 1. every render — catches inputs that change with React state, e.g. the
  //    project boundary arriving after mount (no map event fires for that);
  useEffect(() => {
    const next = getBboxRef.current(map);
    setBbox((prev) => (sameRoundedBbox(prev, next) ? prev : next));
  });

  // 2. debounced moveend — catches viewport moves, which don't re-render.
  useEffect(() => {
    if (!map) return;
    const update = () => {
      const next = getBboxRef.current(map);
      setBbox((prev) => (sameRoundedBbox(prev, next) ? prev : next));
    };
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const onMoveEnd = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(update, 300);
    };
    map.on("moveend", onMoveEnd);
    return () => {
      if (debounce) clearTimeout(debounce);
      map.off("moveend", onMoveEnd);
    };
  }, [map]);

  const { data, isFetching } = useRealtimeObservations(
    kind,
    bbox,
    windowHours,
    active && available
  );

  // Collapse repeat observations of the same pixel before anything else sees
  // the data: a geostationary sensor rescans every ~10 minutes, so the same
  // location arrives dozens of times in a 24-hour window and would otherwise
  // be drawn, counted and clustered as dozens of separate detections.
  //
  // Hazards and the customer-fed kinds skip it on both counts: two
  // incidents at one address are two incidents, and the pass keeps Point
  // features only — it would throw away every warning-area / burnt-area
  // polygon.
  const unique = useMemo(
    () =>
      SKIP_DEDUPE_KINDS.has(kind)
        ? data ?? EMPTY_FC
        : dedupeDetections(data ?? EMPTY_FC),
    [data, kind]
  );

  useEffect(() => {
    setGeojson(applyFilters(unique, filters));
  }, [unique, filters]);

  // Ignitions can only be created where there is a project and regime context
  // (the project map, not the listing map). Both kinds can seed a point
  // ignition — a lightning strike is a classic ignition source — but only
  // hotspots form area fires; a strike is a point, never a polygon.
  const ignitionsEnabled = Boolean(projectIdForIgnitions);
  const clustersEnabled = ignitionsEnabled && kind === "hotspot";

  const clusters = useMemo(
    () => (clustersEnabled ? clusterDetections(geojson, clusterDelta) : []),
    [clustersEnabled, geojson, clusterDelta]
  );

  // The project's incidents (shared cache with the Incidents layer/tab, 60 s
  // poll; off on the listing map, which has no project) so cluster hulls
  // learn which incident(s) they belong to. Markers and footprint hulls are
  // the Incidents layer's job.
  const { data: incidentsData } = useProjectIncidents(
    ignitionsEnabled ? projectIdForIgnitions : undefined
  );
  const kindIncidents = useMemo(
    () => (incidentsData?.data?.incidents ?? []).filter((i) => i.kind === kind),
    [incidentsData, kind]
  );
  const incidentIdsByCluster = useMemo(
    () => matchClustersToIncidents(clusters, kindIncidents),
    [clusters, kindIncidents]
  );

  // Detections are collected from a buffer well beyond the project, so plenty
  // of them (and whole fires) sit outside it. Those cannot become ignitions —
  // the backend rejects them — so the UI says so up front instead of letting
  // the user hit an error.
  const isInsideProject = useCallback(
    (coord: [number, number]) =>
      !projectBoundary || pointInGeometry(coord, projectBoundary),
    [projectBoundary]
  );

  const clusterIsCreatable = useCallback(
    (cluster: { features: { geometry: { coordinates: number[] } }[] }) =>
      cluster.features.some((f) =>
        isInsideProject(f.geometry.coordinates as [number, number])
      ),
    [isInsideProject]
  );

  const displayedGeojson = useMemo<FeatureCollection>(() => {
    if (!active || !available) return EMPTY_FC;
    if (!clusters.length) return geojson;
    // Hulls and detections share one source; each style filters on
    // geometry type, and hulls go first so the points draw on top of them.
    return {
      type: "FeatureCollection",
      features: [
        ...clustersToFeatureCollection(clusters, clusterIsCreatable, (cluster) =>
          incidentIdsByCluster.get(cluster.id)
        ).features,
        ...geojson.features,
      ],
    };
  }, [
    active,
    available,
    geojson,
    clusters,
    clusterIsCreatable,
    incidentIdsByCluster,
  ]);

  // Push data refreshes into the live GeoJSON source instead of flagging the
  // layer stale (a stale re-add tears down and recreates the source, which
  // flickers). If the source hasn't been registered yet this no-ops and the
  // registration path picks up the current data from `source` below.
  useEffect(() => {
    if (!map) return;
    const source = (map as any).getSource(id);
    if (!source || typeof source.setData !== "function") return;
    source.setData(displayedGeojson);
  }, [displayedGeojson, map, id]);

  const layerStyle = useMemo(() => {
    if (kind === "hotspot") {
      return [
        hotspotClusterFillStyle(id),
        hotspotClusterLineStyle(id),
        hotspotLayerStyle(id),
      ];
    }
    if (kind === "hazard") {
      // Areas first so the anchor points draw on top of their own shading.
      return [hazardAreaFillStyle(id), hazardAreaLineStyle(id), hazardLayerStyle(id)];
    }
    if (kind === "burnt_area") {
      return [burntAreaFillStyle(id), burntAreaLineStyle(id), burntAreaLayerStyle(id)];
    }
    if (kind === "ignition") {
      return [ignitionLayerStyle(id)];
    }
    return [lightningLayerStyle(id)];
  }, [kind, id]);
  const interactiveLayerIds = useMemo(
    () => layerStyle.map((style) => style.id),
    [layerStyle]
  );

  const observationCount = useMemo(
    () =>
      geojson.features.filter((f) => f.geometry?.type === "Point").length,
    [geojson]
  );

  // A kind the organisation cannot use has no data behind it, so it must not
  // become visible at all. Guarded here as well as in the sidebar control,
  // because this is what every entry point ends up calling.
  const handleToggle = () => {
    if (!available) return;
    setActive((prev) => !prev);
  };

  // Revoking a kind while its layer is showing must take it off the map too,
  // rather than leaving a stranded visible layer.
  useEffect(() => {
    if (!available) setActive(false);
  }, [available]);

  // External "show yourself" nudge — placing a simulated detection of this
  // kind turns the layer on so the new point is actually visible.
  useEffect(() => {
    if (activateSignal && available) setActive(true);
  }, [activateSignal, available]);

  const navigate = useNavigate();
  const handleClick = (e: any) => {
    if (!e.features || e.features.length === 0) return;
    // Prefer a detection under the cursor over the hull it sits inside.
    const feature =
      e.features.find((f: any) => f.geometry?.type === "Point") ?? e.features[0];

    // Only hotspots draw cluster hulls. Hazard warning areas and burnt-area
    // footprints are polygons too, but clicking one opens the same popover
    // its anchor point would (useful when the point is off screen or under
    // another marker).
    if (kind === "hotspot" && feature.geometry?.type === "Polygon") {
      if (feature.properties?.creatable === false) {
        setOutsideNotice("This fire is outside the project area.");
        return;
      }
      const count = Number(feature.properties?.detection_count) || 0;
      setPendingIgnition({
        kind: "cluster",
        hull: feature.geometry,
        detectionCount: count,
        label: `Hotspot cluster (${count} detections)`,
      });
      return;
    }

    // Mapbox hands back tile-clipped geometry for polygons; for an area
    // feature take the whole shape from the source data (matched by id) so
    // an ignition made from it is the full footprint.
    const areaGeometry = feature.properties?.is_area
      ? geojson.features.find((f) => f.id === feature.id)?.geometry ??
        feature.geometry
      : undefined;
    setSelectedProps({
      ...(feature.properties ?? {}),
      // Mapbox flattens properties only; keep the geometry for the
      // create-ignition action, and the feature id (the observation's DB id)
      // for removing simulated detections.
      __coordinates: feature.geometry?.coordinates,
      __id: feature.id,
      __area_geometry: areaGeometry,
    });
    setPopoverPosition(mapEventScreenPoint(e));
  };

  const handleClosePopover = () => {
    setSelectedProps(null);
    setPopoverPosition(null);
  };

  // Simulated detections can be removed ahead of their expiry. The backend
  // only matches rows behind the caller's tenant's simulated source, so this
  // can never touch real provider data.
  const deleteSimulated = useDeleteSimulatedDetection();
  const selectedSimulatedId =
    selectedProps?.simulated && selectedProps.__id !== undefined
      ? Number(selectedProps.__id)
      : null;
  const handleDeleteSimulated = () => {
    if (selectedSimulatedId === null) return;
    deleteSimulated.mutate(
      { id: selectedSimulatedId, kind },
      { onSuccess: handleClosePopover }
    );
  };

  // Manual incident creation from a detection (project map only). The escape
  // hatch for fires that predate the project or predate detections being
  // enabled — the pipeline's freshness gate never creates incidents for
  // those on its own.
  const createIncident = useCreateIncidentFromDetection(projectIdForIgnitions);
  const [incidentNotice, setIncidentNotice] = useState<{
    message: string;
    severity: "success" | "info" | "error";
    incidentId?: number;
  } | null>(null);
  const handleCreateIncident = () => {
    const observationId = Number(selectedProps?.__id);
    if (!Number.isFinite(observationId)) return;
    createIncident.mutate(observationId, {
      onSuccess: (result) => {
        handleClosePopover();
        setIncidentNotice(
          result.created
            ? {
                message: "Incident created — it now appears in this project's Incidents tab.",
                severity: "success",
                incidentId: result.incident.id,
              }
            : {
                message: "This detection is already covered by an open incident.",
                severity: "info",
                incidentId: result.incident.id,
              }
        );
      },
      onError: (err) =>
        setIncidentNotice({
          message:
            (err as { message?: string })?.message ||
            "Could not create the incident.",
          severity: "error",
        }),
    });
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      enabled={true}
      toggledOn={active}
      icon={KIND_META[kind].icon}
      onClick={handleToggle}
    />
  );

  const handleCreatePointIgnition = () => {
    if (!selectedProps) return;
    const coordinates = selectedProps.__coordinates as [number, number] | undefined;
    if (!coordinates) return;
    const observed = selectedProps.observed_at
      ? formatInTimezone(selectedProps.observed_at, selectedTimezone, "D MMM HH:mm")
      : "";
    const detail =
      kind === "hotspot"
        ? selectedProps.satellite
          ? ` ${selectedProps.satellite}`
          : ""
        : kind !== "lightning" && selectedProps.title
        ? ` ${selectedProps.title}`
        : "";
    const source =
      kind === "hotspot"
        ? "Hotspot"
        : kind === "lightning"
        ? "Lightning"
        : kind === "ignition"
        ? "Ignition"
        : kind === "burnt_area"
        ? "Burnt area"
        : "Incident";
    setPendingIgnition({
      kind: "point",
      coordinates,
      label: `${source}${detail}${observed ? ` ${observed}` : ""}`.slice(0, 255),
    });
    handleClosePopover();
  };

  // Burnt-area footprint → polygon ignition (the fire starts from the whole
  // burnt area). Reuses the cluster-hull path of the ignition dialog.
  const handleCreateAreaIgnition = () => {
    if (!selectedProps) return;
    const hull = largestPolygon(selectedProps.__area_geometry as Geometry | undefined);
    if (!hull) return;
    const observed = selectedProps.observed_at
      ? formatInTimezone(selectedProps.observed_at, selectedTimezone, "D MMM HH:mm")
      : "";
    setPendingIgnition({
      kind: "cluster",
      hull,
      detectionCount: 0,
      label: `Burnt area${selectedProps.title ? ` ${selectedProps.title}` : ""}${
        observed ? ` ${observed}` : ""
      }`.slice(0, 255),
    });
    handleClosePopover();
  };

  // A warning-area polygon carries its anchor's properties but not a single
  // coordinate, so it offers information only — the actions belong to the
  // anchor point, which is always on the map too.
  const selectedIsArea = Boolean(selectedProps?.is_area);
  const selectedCoords = selectedIsArea
    ? undefined
    : (selectedProps?.__coordinates as [number, number] | undefined);
  const selectedIsInsideProject = selectedCoords
    ? isInsideProject(selectedCoords)
    : false;
  const actionsEnabled = Boolean(selectedProps) && !selectedIsArea;

  const popover = (
    <>
      <RealtimeObservationPopover
        kind={kind}
        properties={selectedProps}
        anchorPosition={popoverPosition}
        onClose={handleClosePopover}
        onCreateIgnition={
          ignitionsEnabled && actionsEnabled && selectedIsInsideProject
            ? handleCreatePointIgnition
            : undefined
        }
        blockedReason={
          ignitionsEnabled && actionsEnabled && !selectedIsInsideProject
            ? "Outside the project area — ignitions must be inside it."
            : undefined
        }
        onDeleteSimulated={
          selectedSimulatedId !== null ? handleDeleteSimulated : undefined
        }
        deleteInProgress={deleteSimulated.isPending}
        onCreateIncident={
          projectIdForIgnitions !== undefined && actionsEnabled
            ? handleCreateIncident
            : undefined
        }
        createIncidentInProgress={createIncident.isPending}
        onCreateAreaIgnition={
          ignitionsEnabled &&
          kind === "burnt_area" &&
          selectedIsArea &&
          Boolean(selectedProps?.__area_geometry)
            ? handleCreateAreaIgnition
            : undefined
        }
      />
      {incidentNotice && (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => setIncidentNotice(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={incidentNotice.severity}
            onClose={() => setIncidentNotice(null)}
            action={
              incidentNotice.incidentId !== undefined ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    navigate(
                      `/projects/${projectIdForIgnitions}/incidents/${incidentNotice.incidentId}`
                    )
                  }
                >
                  View
                </Button>
              ) : undefined
            }
          >
            {incidentNotice.message}
          </Alert>
        </Snackbar>
      )}
      {outsideNotice && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setOutsideNotice(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="info" onClose={() => setOutsideNotice(null)}>
            {outsideNotice} Ignitions must be inside it.
          </Alert>
        </Snackbar>
      )}
      {ignitionsEnabled && projectIdForIgnitions !== undefined && (
        <CreateIgnitionFromDetectionsDialog
          open={Boolean(pendingIgnition)}
          onClose={() => setPendingIgnition(null)}
          projectId={projectIdForIgnitions}
          pending={pendingIgnition}
        />
      )}
    </>
  );

  const customComponent = active ? (
    <RealtimeLegend
      kind={kind}
      windowHours={windowHours}
      onWindowChange={setWindowHours}
      // Anchor points only: hazard warning areas ride along as sibling
      // polygon features and would otherwise be counted as extra incidents.
      count={active && available ? observationCount : 0}
      isLoading={isFetching && !data}
      scopeNote={scopeNote}
      clusterDelta={clustersEnabled ? clusterDelta : undefined}
      onClusterDeltaChange={clustersEnabled ? setClusterDelta : undefined}
      clusterCount={clusters.length}
    />
  ) : null;

  return {
    id,
    name,
    source: displayedGeojson,
    rawSource: unique,
    layerStyle,
    toolbarButton,
    active,
    stale,
    setStale,
    interactiveLayerIds,
    onClick: handleClick,
    popover,
    customComponent,
  };
};

export default RealtimeObservationsLayer;
